/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  HOC for deriving props from the current route
 */
import { withRouter } from '@zcode/router-utils'
import * as H from 'history'
import hoistStatics from 'hoist-non-react-statics'
import React, { Component, ComponentClass } from 'react'
import {
  GetProps,
  InferableComponentEnhancerWithProps,
  Matching,
  shallowEqual,
} from 'react-redux'
import { type StaticContext } from 'react-router'
import { RouteComponentProps } from 'react-router-dom'

export type MapRouteAndOwnPropsToPropsParam<
  RouteProps,
  Params extends { [K in keyof Params]?: string },
  OwnProps,
  RouteLocationState,
> = (
  routeProps: RouteComponentProps<Params, StaticContext, RouteLocationState>,
  ownProps: OwnProps
) => RouteProps

export interface RouterConnectOptions {
  forwardRouteProps: boolean
}

export interface RouterConnect {
  <
    RouteProps extends object = object,
    Params extends object = object,
    OwnProps extends object = object,
    RouteLocationState = H.LocationState,
  >(
    mapRouteToProps: MapRouteAndOwnPropsToPropsParam<
      RouteProps,
      Params,
      OwnProps,
      RouteLocationState
    >,
    options: { forwardRouteProps: true }
  ): InferableComponentEnhancerWithProps<
    RouteProps & RouteComponentProps<Params, StaticContext, RouteLocationState>,
    OwnProps
  >
  <
    RouteProps extends object = object,
    Params extends object = object,
    OwnProps extends object = object,
    RouteLocationState = H.LocationState,
  >(
    mapRouteToProps: MapRouteAndOwnPropsToPropsParam<
      RouteProps,
      Params,
      OwnProps,
      RouteLocationState
    >,
    options?: { forwardRouteProps: false }
  ): InferableComponentEnhancerWithProps<RouteProps, OwnProps>
  <
    RouteProps extends object = object,
    Params extends object = object,
    OwnProps extends object = object,
    RouteLocationState = H.LocationState,
  >(
    mapRouteToProps: MapRouteAndOwnPropsToPropsParam<
      RouteProps,
      Params,
      OwnProps,
      RouteLocationState
    >,
    options?: RouterConnectOptions
  ): InferableComponentEnhancerWithProps<
    RouteProps &
      Partial<RouteComponentProps<Params, StaticContext, RouteLocationState>>,
    OwnProps
  >
}

/**
 * @deprecated Use react-router hooks within functional components instead.
 */
export const routerConnect: RouterConnect = <
  RouteProps extends object = object,
  Params extends object = object,
  OwnProps extends object = object,
  RouteLocationState = H.LocationState,
>(
  mapRouteToProps: MapRouteAndOwnPropsToPropsParam<
    RouteProps,
    Params,
    OwnProps,
    RouteLocationState
  >,
  options?: RouterConnectOptions
): any =>
  (<C extends ComponentClass<Matching<RouteProps, GetProps<C>>>>(
    WrappedComponent: C
  ) => {
    type Props = RouteComponentProps<
      Params,
      StaticContext,
      RouteLocationState
    > &
      OwnProps & { wrappedComponentRef?: React.Ref<InstanceType<C>> }
    class RouterConnect extends Component<Props> {
      static displayName = `routerConnect(${
        WrappedComponent.displayName ?? WrappedComponent.name
      })`

      static WrappedComponent = WrappedComponent
      private mappedProps: RouteProps | undefined
      private prevLocation: H.Location | undefined
      private prevOwnProps:
        | Omit<Props, 'history' | 'location' | 'match' | 'wrappedComponentRef'>
        | undefined

      componentWillUnmount() {
        this.mappedProps = undefined
        this.prevLocation = undefined
        this.prevOwnProps = undefined
      }

      getMappedProps() {
        const { history, location, match, wrappedComponentRef, ...ownProps } =
          this.props

        // only compare ownProps when mapRouteToProps uses it
        const shouldCompareOwnProps = mapRouteToProps.length > 1
        const ownPropsUpdated =
          shouldCompareOwnProps && !shallowEqual(ownProps, this.prevOwnProps)

        // recalculate mappedProps if location or ownProps changes
        if (location !== this.prevLocation || ownPropsUpdated) {
          this.mappedProps = mapRouteToProps(
            { history, location, match },
            ownProps as any
          )
          this.prevLocation = location
          this.prevOwnProps = ownProps
        }
        return this.mappedProps
      }

      render() {
        const mappedProps = this.getMappedProps()

        // adds forwarded props
        let { wrappedComponentRef, ...remainingProps } = this.props as any

        if (!options?.forwardRouteProps) {
          const { history, location, match, staticContext, ...restOfProps } =
            remainingProps
          remainingProps = restOfProps
        }

        return (
          <WrappedComponent
            {...remainingProps}
            {...mappedProps}
            ref={wrappedComponentRef}
          />
        )
      }
    }

    hoistStatics(RouterConnect, WrappedComponent)
    // @ts-expect-error
    return withRouter(RouterConnect)
  }) as unknown as InferableComponentEnhancerWithProps<RouteProps, OwnProps>
